import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/admin.api';
import { ActiveUserItem } from '../../types/Admin.api.type';
import { Users, Minus, Plus } from 'lucide-react';

/**
 * 활성 사용자 목록 (출석+투표+로그인 합산 N회 이상)
 */
export const ActiveUserList: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [minCount, setMinCount] = useState(3);
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['activeUsers', currentPage, minCount],
    queryFn: () => adminApi.getActiveUsers(currentPage, pageSize, minCount).then(res => res.data),
  });

  const handleMinCountChange = (delta: number) => {
    setMinCount(prev => Math.max(1, prev + delta));
    setCurrentPage(0);
  };

  const items: ActiveUserItem[] = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return <div className="p-6 text-center text-gray-500">로딩 중...</div>;
  }

  return (
    <div className="space-y-4">
      {/* 헤더 */}
      <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-5 h-5 text-blue-500" />
          <h2 className="text-lg lg:text-xl font-bold text-[#030213]">활성 사용자</h2>
        </div>
        <div className="flex items-center gap-2 text-sm text-[#6B7280]">
          <span>출석 + 투표 + 로그인 합산</span>
          <div className="inline-flex items-center gap-1">
            <button
              onClick={() => handleMinCountChange(-1)}
              disabled={minCount <= 1}
              className="w-6 h-6 flex items-center justify-center rounded-full border border-[#E5E7EB] hover:bg-[#f3f3f5] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <Minus className="w-3 h-3" />
            </button>
            <span className="min-w-[2ch] text-center font-bold text-[#030213]">{minCount}</span>
            <button
              onClick={() => handleMinCountChange(1)}
              className="w-6 h-6 flex items-center justify-center rounded-full border border-[#E5E7EB] hover:bg-[#f3f3f5] transition-colors"
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
          <span>회 이상 사용자 · 총 {totalElements.toLocaleString()}명</span>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#f3f3f5]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">사용자</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] uppercase tracking-wider">출석</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] uppercase tracking-wider">투표</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] uppercase tracking-wider">로그인</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] uppercase tracking-wider">합계</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7280] uppercase tracking-wider">최근 활동</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E5E7EB]">
              {items.map((item, idx) => (
                <tr key={item.userId} className="hover:bg-[#f3f3f5] transition-colors">
                  <td className="px-4 py-4 text-sm font-medium text-[#6B7280]">
                    {currentPage * pageSize + idx + 1}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#f3f3f5] flex items-center justify-center overflow-hidden">
                        {item.profileImageUrl ? (
                          <img src={item.profileImageUrl} alt={item.userName} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <span className="text-[#6B7280] font-medium text-xs">
                            {item.userName?.charAt(0)?.toUpperCase() ?? '?'}
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-medium text-[#030213]">{item.userName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center text-sm text-[#030213]">{item.attendanceCount}</td>
                  <td className="px-4 py-4 text-center text-sm text-[#030213]">{item.voteCount}</td>
                  <td className="px-4 py-4 text-center text-sm text-[#030213]">{item.loginCount}</td>
                  <td className="px-4 py-4 text-center">
                    <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-700">
                      {item.totalCount}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right text-sm text-[#6B7280]">
                    {formatDate(item.lastActivityAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 페이징 */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 p-4 border-t border-[#E5E7EB]">
            <button
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - 5));
              const pageNum = startPage + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1 text-sm rounded ${
                    currentPage === pageNum
                      ? 'bg-[#030213] text-white'
                      : 'border border-[#E5E7EB] hover:bg-[#f3f3f5]'
                  }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
              disabled={currentPage >= totalPages - 1}
              className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-2">
        {items.map((item, idx) => (
          <div key={item.userId} className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs text-[#6B7280] font-medium">#{currentPage * pageSize + idx + 1}</span>
              <div className="w-8 h-8 rounded-full bg-[#f3f3f5] flex items-center justify-center overflow-hidden">
                {item.profileImageUrl ? (
                  <img src={item.profileImageUrl} alt={item.userName} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <span className="text-[#6B7280] font-medium text-xs">
                    {item.userName?.charAt(0)?.toUpperCase() ?? '?'}
                  </span>
                )}
              </div>
              <span className="text-sm font-medium text-[#030213] flex-1">{item.userName}</span>
              <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-700">
                {item.totalCount}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center mb-2">
              <div>
                <p className="text-xs text-[#6B7280]">출석</p>
                <p className="text-sm font-bold text-[#030213]">{item.attendanceCount}</p>
              </div>
              <div>
                <p className="text-xs text-[#6B7280]">투표</p>
                <p className="text-sm font-bold text-[#030213]">{item.voteCount}</p>
              </div>
              <div>
                <p className="text-xs text-[#6B7280]">로그인</p>
                <p className="text-sm font-bold text-[#030213]">{item.loginCount}</p>
              </div>
            </div>
            <p className="text-xs text-[#6B7280] text-right">{formatDate(item.lastActivityAt)}</p>
          </div>
        ))}

        {/* 모바일 더보기 */}
        {items.length < totalElements && (
          <button
            onClick={() => setCurrentPage(prev => prev + 1)}
            className="w-full py-3 text-sm text-[#6B7280] bg-white rounded-lg shadow-sm hover:bg-[#f3f3f5] transition-colors"
          >
            더보기 ({items.length} / {totalElements})
          </button>
        )}
      </div>
    </div>
  );
};
